/* eslint-disable @typescript-eslint/naming-convention */
import { forwardRef, ReactNode, FocusEvent, useState, useEffect } from 'react'

import { cva, VariantProps } from 'class-variance-authority'

import { maskTypingMobileNumber } from '@erudilabs/format-utils'
import { mergeClassName } from '@erudilabs/merge-class-utils'

const mobileInput = cva(
  [
    'border w-full transition-all h-10 rounded-md text-sm pr-4 placeholder-gray-600 pl-16',
    'bg-white border-gray-300 text-black focus:outline-blue-500',
    'dark:bg-gray-900 dark:border-gray-700 dark:text-white dark:focus:outline-blue-300',
  ],
  {
    variants: {
      disabled: {
        true: [
          'cursor-not-allowed',
          'bg-gray-200 border-gray-300 text-gray-600 hover:bg-gray-200 disabled:placeholder-gray-500',
          'dark:bg-gray-800 dark:border-gray-800 dark:text-gray-600 dark:hover:bg-gray-800 dark:disabled:placeholder-gray-700',
        ],
      },
    },
    defaultVariants: {
      disabled: false,
    },
  }
)

type CountryCodeProps = '62' | '63'

export interface MobileInputProps extends VariantProps<typeof mobileInput> {
  className?: string
  testId?: string
  id?: string
  label?: string
  name: string
  helper?: ReactNode
  defaultValue?: string
  countryCode: CountryCodeProps
  value?: string
  readOnly?: boolean
  placeholder?: string
  errorMessage?: string
  hideErrorMessage?: boolean
  autoFocus?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (e: any) => void
  onBlur?: (e: FocusEvent) => void
}

export const MobileInput = forwardRef<HTMLInputElement, MobileInputProps>(
  (props, ref) => {
    const {
      readOnly,
      name,
      testId,
      id,
      onChange,
      onBlur,
      defaultValue,
      autoFocus,
      value,
      errorMessage,
      hideErrorMessage,
      placeholder,
      countryCode: prefix,
      helper,
      label,
      disabled,
      className,
    } = props

    const maskInputAmount = (str?: string | null) => {
      if (!str) return undefined
      if (prefix === '63') {
        return maskTypingMobileNumber(str, { countryCode: 'PH' })
      } else {
        return maskTypingMobileNumber(str, { countryCode: 'ID' })
      }
    }

    const [maskedValue, setMaskedValue] = useState<number | string>(
      value || defaultValue || ''
    )

    const handleChange = (str: string) => {
      const masked = maskInputAmount(str)
      onChange && onChange(masked || '')
      setMaskedValue(masked || '')
    }

    useEffect(() => {
      if (value !== maskedValue) {
        setMaskedValue(value as string)
      }
    }, [value])

    return (
      <div className={mergeClassName('relative', className)}>
        <div
          className={mergeClassName(
            label ? 'top-[38px]' : 'top-1/2 -translate-y-1/2',
            disabled
              ? 'bg-gray-300 dark:bg-gray-700 text-gray-600'
              : 'bg-blue-100 border-blue-200 text-blue-800 dark:bg-blue-300 dark:border-blue-400 dark:text-blue-800',
            'absolute left-3 font-semibold rounded-full px-2'
          )}
        >
          +{prefix}
        </div>

        {label && (
          <label
            htmlFor={name}
            className={mergeClassName(
              errorMessage
                ? 'text-red-600 dark:text-red-400'
                : 'text-black dark:text-white',
              'mb-2 block cursor-pointer text-left font-semibold'
            )}
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          readOnly={readOnly}
          data-testid={testId}
          value={maskTypingMobileNumber((maskedValue as string) || '', {
            countryCode: prefix === '63' ? 'PH' : 'ID',
          })}
          name={name}
          autoFocus={autoFocus}
          placeholder={placeholder}
          type="tel"
          className={mergeClassName(
            mobileInput({
              disabled,
            }),
            errorMessage && 'border-red-500 dark:border-red-400'
          )}
          disabled={disabled as boolean}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={onBlur}
          autoComplete="off"
        />
        {helper && (
          <p className="mt-2 text-left text-xs font-semibold text-gray-600 first-letter:capitalize">
            {helper}
          </p>
        )}
        {errorMessage && !hideErrorMessage && (
          <p className="mt-2 text-left text-xs font-semibold text-red-600 dark:text-red-400 first-letter:capitalize">
            {errorMessage}
          </p>
        )}
      </div>
    )
  }
)
